import { useState, useCallback } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'

// v2.0: localStorage key rename. The new key inverts the boolean — `true`
// means "collapsed" (the transient *action* state) rather than "open" (the
// resting *result* state). This matches the dominant 2025-2026 pattern where
// the persistence key tracks the *action* rather than the *result*.
const STORAGE_KEY_COLLAPSED = 'fintrack:sidebar-collapsed'

// Legacy v1.2.0 key — read once on first mount, then deleted. After this
// deployment, no code path writes to it. Kept as a constant so the
// migration logic in `migrateLegacyKey` is explicit.
const LEGACY_KEY_OPEN = 'fintrack:sidebar-open'

const STORAGE_KEY_PINNED = 'fintrack:sidebar-pinned'

/**
 * Typed localStorage boolean reader. Returns `defaultValue` if storage is empty,
 * unavailable, or contains an unexpected value.
 */
function readBoolean(key: string, defaultValue: boolean): boolean {
  try {
    const stored = localStorage.getItem(key)
    if (stored === 'true') return true
    if (stored === 'false') return false
    return defaultValue
  } catch {
    return defaultValue
  }
}

/**
 * Typed localStorage boolean writer. Stores the value as the literal strings
 * 'true' / 'false' (matching the readBoolean contract above). Fails silently
 * if storage is unavailable (private mode, quota exceeded, SSR, etc.).
 */
function writeBoolean(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? 'true' : 'false')
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * One-shot migration from v1.2.0's `fintrack:sidebar-open` key to v2.0's
 * `fintrack:sidebar-collapsed` key.
 *
 * IDEMPOTENT: a second call is a no-op (the new key is now present).
 * LOSSLESS: the boolean is inverted exactly (open=true ↔ collapsed=false).
 * TRANSPARENT: no user prompt, no UI flicker, no reload.
 *
 * Side effects:
 *   - writes `STORAGE_KEY_COLLAPSED` if a legacy value is found
 *   - removes `LEGACY_KEY_OPEN`
 *   - logs a dev-only console.info (suppressed in production builds)
 *
 * @see https://github.com/fintrack/frontend/src/hooks/useItemCountPreference.ts
 */
function migrateLegacyKey(): void {
  try {
    // Skip if the new key is already populated (subsequent calls).
    if (localStorage.getItem(STORAGE_KEY_COLLAPSED) !== null) return

    // Read the legacy key.
    const legacy = localStorage.getItem(LEGACY_KEY_OPEN)
    if (legacy === null) return // No legacy data; nothing to migrate.

    // Invert: legacy "open=true" → new "collapsed=false".
    const inverted = legacy !== 'true'
    writeBoolean(STORAGE_KEY_COLLAPSED, inverted)
    localStorage.removeItem(LEGACY_KEY_OPEN)
    // Dev-only log for traceability. `import.meta.env.DEV` is provided by
    // Vite (always available in both dev and prod builds); `false` in
    // production lets Rollup dead-code-eliminate the entire branch.
    if (import.meta.env?.DEV) {
      console.info(
        `[useSidebarPreference] migrated legacy key ${LEGACY_KEY_OPEN}="${legacy}" → ${STORAGE_KEY_COLLAPSED}="${inverted}"`,
      )
    }
  } catch {
    // Silently ignore; treat as no-op.
  }
}

/**
 * useSidebarPreference — localStorage-backed sidebar state persistence
 *
 * Persists TWO dimensions:
 *   - `isCollapsed` — sidebar collapsed vs expanded (collapsible)
 *   - `pinned`      — sidebar locked open (v1.2.0 PR2)
 *
 * Priority on page load: localStorage > defaults.
 *
 * Defaults:
 *   - `isCollapsed` = `false` (sidebar starts expanded)
 *   - `pinned`      = `!isMobile` — `true` on desktop, `false` on mobile
 *     (viewport-aware: ensures user always has an exit path from the pinned
 *      state via the pin toggle, which stays visible at all times)
 *
 * v2.0 migration: on first mount after deploy, if `fintrack:sidebar-collapsed`
 * is absent but `fintrack:sidebar-open` is present, we migrate the legacy
 * value (with inversion) and delete the legacy key.
 *
 * Persistence pattern follows langflow-ai/langflow PR #9597 (typed
 * "true"/"false" string comparisons rather than JSON.parse).
 *
 * @see https://github.com/fintrack/frontend/src/hooks/useItemCountPreference.ts
 * @see https://github.com/fintrack/frontend/src/hooks/useFilterUrlSync.ts
 */
export function useSidebarPreference() {
  const isMobile = useIsMobile()

  // v2.0: initialize from the new key; trigger one-shot migration from legacy.
  // Migration runs BEFORE the initializer reads (we call migrateLegacyKey first
  // so the new key will already be populated if legacy data existed).
  const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => {
    migrateLegacyKey()
    return readBoolean(STORAGE_KEY_COLLAPSED, false) // default = expanded
  })

  // v1.2.0 fix: viewport-aware default — desktop starts pinned (locked open),
  // mobile starts unpinned (overlay behavior). This guarantees the user always
  // has a way out: when pinned, the pin toggle remains visible so they can unpin.
  const [pinned, setPinnedState] = useState<boolean>(() => readBoolean(STORAGE_KEY_PINNED, !isMobile))

  const setCollapsed = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setIsCollapsedState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value
      writeBoolean(STORAGE_KEY_COLLAPSED, next)
      return next
    })
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev)
  }, [setCollapsed])

  const setPinned = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setPinnedState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value
      writeBoolean(STORAGE_KEY_PINNED, next)
      return next
    })
  }, [])

  const togglePinned = useCallback(() => {
    setPinned((prev) => !prev)
  }, [setPinned])

  return { isCollapsed, setCollapsed, toggleCollapsed, pinned, setPinned, togglePinned } as const
}
